import { WorkspacePanelDto, WorkspacePanelTypeEnumType } from '@mark43/cad-api';
import type { DexDispatcherFormEnumType } from '@mark43/cad-api';
import { action, computed, makeObservable, observable } from 'mobx';
import { WorkspaceQueue } from './workspace-queue';
import { v4 as uuidv4, v4 } from 'uuid';
import { minimumDimensionsByPanelType } from './panel-configuration';
import type {
    ClientWorkspacePanel,
    PanelStateForWorkspacePanelType,
} from './workspace-panel-types';

const MIN_X_POSITION = 0;
const MIN_Y_POSITION = 0;

export interface PanelActionDelegate {
    removePanel(panel: WorkspacePanel, onCloseCallback?: () => void): void;
    bringPanelToFront(panel: WorkspacePanel): void;
    isPanelFocused(panel: WorkspacePanel): boolean;
    enqueueSaveWorkspaceWindow: (workspaceWindowId: number) => void;
    syncInactivityAlertSound(): void;
}

type WorkspacePanelDtoWithAlteredQueue = Omit<WorkspacePanelDto, 'queue' | 'zindex'> & {
    zindex?: number;
    queue: WorkspaceQueue | undefined;
};

export class WorkspacePanel<T extends WorkspacePanelTypeEnumType | unknown = unknown>
    implements WorkspacePanelDtoWithAlteredQueue {
    public windowId: number;

    public workspacePanelType: WorkspacePanelDto['workspacePanelType'];

    public linkedEntityType: WorkspacePanelDto['linkedEntityType'];

    @observable
    public linkedEntityId: WorkspacePanelDto['linkedEntityId'];

    @observable
    public xcoordinate: number;

    @observable
    public ycoordinate: number;

    @observable
    public width: number;

    @observable
    public height: number;

    public temporaryId?: WorkspacePanelDto['temporaryId'];

    @observable
    public zindex: number | undefined;

    @observable
    public lastFocusedTime: number | undefined;

    /**
     * Client only state for panel
     *  - useful to open a panel in a particular state (via workspacePanelConfig) that isn't captured in other WorkspacePanel properties.
     *  - state is observable, use updatePanelState to update field in state, if panel needs to be rerendered for state field change.
     *  - if the state value will be loaded/saved to backend (basically all fields in WorkspacePanelDto and other Dto fields), DO NOT put in this object.
     */
    @observable
    private panelState: PanelStateForWorkspacePanelType<T> = {};

    public minimumDimensions: [number, number];

    public id: WorkspacePanelDto['id'];

    public dexForm?: DexDispatcherFormEnumType;

    public dexNextFormId?: string;

    public dexNextFormName?: string;

    public queue: WorkspaceQueue | undefined;

    private delegate: PanelActionDelegate;

    private synchronizeLayoutListener?: (...args: any[]) => any;

    public onBroughtToFront: (() => void) | undefined;

    /**
     * This refers to whichever id the panel had when it was constructed. A panel can gain an id during its lifetime (when a newly created panel is first
     *   saved as part of a workspace) but we prefer to keep a stable value that can be referenced in many places so as to prevent superfluous re-rendering.
     *   Having a stable value allows us to avoid moving items around in WorkspacePanelsStore.panelsById if they gain a real id during their lifetime (at which
     *   point the panels "real id" shifts from temporaryId to id).
     */
    public initialId: string | number;
    /**
     * Enables us to identify a given panel. Useful in correspondence with action broadcaster to target specific
     *  panels with actions.
     */
    public uuid: string;

    // draft alert logic
    private inactivityTimerSeconds: number = 0;

    private inactivityTimeoutId: number | null = null;

    private clearInactivityTimeout(): void {
        if (this.inactivityTimeoutId !== null) {
            clearTimeout(this.inactivityTimeoutId);
            this.inactivityTimeoutId = null;
        }
    }

    private clearInactivityAlertTreatment(): void {
        this.setIsAlerting(false);
        this.delegate.syncInactivityAlertSound();
    }

    public startInactivityTimer(timeoutSeconds: number): void {
        this.inactivityTimerSeconds = timeoutSeconds;

        if (this.inactivityTimerSeconds === 0) {
            this.stopInactivityTimer();
            return;
        }

        this.clearInactivityAlertTreatment();
        this.restartInactivityTimer();
    }

    private restartInactivityTimer(): void {
        this.clearInactivityTimeout();

        if (this.inactivityTimerSeconds === 0) {
            return;
        }

        this.inactivityTimeoutId = window.setTimeout(() => {
            if (!this.isAlerting) {
                this.setIsAlerting(true);
                this.delegate.syncInactivityAlertSound();
            }
        }, this.inactivityTimerSeconds * 1000);
    }

    public notifyActivity(): void {
        if (this.inactivityTimerSeconds === 0) {
            return;
        }

        this.clearInactivityAlertTreatment();
        this.restartInactivityTimer();
    }

    public stopInactivityTimer(): void {
        this.inactivityTimerSeconds = 0;
        this.clearInactivityTimeout();
        this.clearInactivityAlertTreatment();
    }

    constructor(configuration: ClientWorkspacePanel<T>, delegate: PanelActionDelegate) {
        this.delegate = delegate;
        this.panelState =
            configuration.panelState ?? ({} as PanelStateForWorkspacePanelType<unknown>);
        this.uuid = configuration.uuid ?? v4();

        this.minimumDimensions = minimumDimensionsByPanelType[configuration.workspacePanelType] ?? [
            450,
            200,
        ];

        this.xcoordinate = configuration.xcoordinate ?? MIN_X_POSITION;
        this.ycoordinate = configuration.ycoordinate ?? MIN_Y_POSITION;

        this.windowId = configuration.windowId;
        this.zindex = configuration.zindex;
        this.width = Math.max(configuration.width, this.minimumDimensions[0]);
        this.height = Math.max(configuration.height, this.minimumDimensions[1]);
        this.id = configuration.id;
        if (!this.id) {
            this.temporaryId = uuidv4();
        }
        this.initialId = (this.id ?? this.temporaryId) as string | number;
        this.workspacePanelType = configuration.workspacePanelType;
        this.linkedEntityType = configuration.linkedEntityType;
        this.linkedEntityId = configuration.linkedEntityId;
        const windowId = this.windowId;
        this.queue = configuration.queue
            ? new WorkspaceQueue(configuration.queue, {
                  ...delegate,
                  enqueueSaveWorkspaceWindow() {
                      delegate.enqueueSaveWorkspaceWindow(windowId);
                  },
              })
            : undefined;
        this.dexForm = configuration.dexForm;
        this.dexNextFormId = configuration.dexNextFormId;
        this.dexNextFormName = configuration.dexNextFormName;

        makeObservable(this);
    }

    public getDto(): WorkspacePanelDto {
        return {
            id: this.id,
            temporaryId: this.temporaryId,
            linkedEntityId: this.linkedEntityId,
            linkedEntityType: this.linkedEntityType,
            height: this.height,
            width: this.width,
            windowId: this.windowId,
            workspacePanelType: this.workspacePanelType,
            xcoordinate: this.xcoordinate,
            ycoordinate: this.ycoordinate,
            zindex: this.zindex!,
            dexForm: this.dexForm,
            dexNextFormId: this.dexNextFormId,
            dexNextFormName: this.dexNextFormName,
            queue: this.queue?.getDto(),
        };
    }

    @observable
    public isAlerting = false;

    @action
    public setIsAlerting(value: boolean): void {
        this.isAlerting = value;
    }

    getPanelState<K extends keyof PanelStateForWorkspacePanelType<T>>(
        stateKey: K
    ): PanelStateForWorkspacePanelType<T>[K] {
        return this.panelState[stateKey];
    }
    @action updatePanelState<K extends keyof PanelStateForWorkspacePanelType<T>>(
        key: K,
        value: PanelStateForWorkspacePanelType<T>[K]
    ): void {
        this.panelState[key] = value;
    }

    @action updateDexForm(dexForm: DexDispatcherFormEnumType) {
        this.dexForm = dexForm;
    }

    public setOnBroughtToFront(handler?: () => void): void {
        this.onBroughtToFront = handler;
    }

    /**
     * Closes this panel
     */
    @action close(onCloseCallback?: () => void): void {
        this.delegate.removePanel(this, () => {
            this.stopInactivityTimer();
            onCloseCallback?.();
        });
    }

    /**
     * Focuses this panel
     */
    @action focus(): void {
        this.lastFocusedTime = Date.now();
        this.delegate.bringPanelToFront(this);
    }

    @computed
    get isFocused(): boolean {
        return this.delegate.isPanelFocused(this);
    }

    getIsFocused(): boolean {
        return this.delegate.isPanelFocused(this);
    }

    @action
    public setCoordinates(xcoordinate: number, ycoordinate: number): void {
        this.xcoordinate = Math.max(xcoordinate, MIN_X_POSITION);
        this.ycoordinate = Math.max(ycoordinate, MIN_Y_POSITION);
        this.delegate.enqueueSaveWorkspaceWindow(this.windowId);
    }

    @action
    public setDimensions(width: number, height: number): void {
        this.width = Math.max(width, this.minimumDimensions[0]);
        this.height = Math.max(height, this.minimumDimensions[1]);
        this.delegate.enqueueSaveWorkspaceWindow(this.windowId);
    }

    public setSynchronizeLayoutListener(listener: typeof this.synchronizeLayoutListener): void {
        this.synchronizeLayoutListener = listener;
    }

    public removeSynchronizeLayoutListener(): void {
        this.synchronizeLayoutListener = undefined;
    }

    @action setLinkedEntityId(linkedEntityId: number): void {
        this.linkedEntityId = linkedEntityId;
    }

    @action setLinkedEntityType(linkedEntityType: WorkspacePanelDto['linkedEntityType']): void {
        this.linkedEntityType = linkedEntityType;
    }

    @action setWorkspacePanelType(
        workspacePanelType: WorkspacePanelDto['workspacePanelType']
    ): void {
        this.workspacePanelType = workspacePanelType;
    }

    @action synchronizeLayout(): void {
        if (this.synchronizeLayoutListener) {
            this.synchronizeLayoutListener();
        }
    }

    @action setWidth(width: number): void {
        this.width = width;
    }
}
